/**
 * Shared outlet-context shapes for the workshop detail page and its nested
 * `checkin` route. Extracted from WorkshopDetailPage so the page's helper
 * components can each live in their own file (react/no-multi-comp) without a
 * circular import back through the page module.
 */
import { useLocation, useOutletContext } from 'react-router-dom';

import type { WorkshopDetail } from '../../../../api/workshops';
import { modeFromPath, type ViewMode } from '../../../../shared/nav/viewMode';

/**
 * Which mode this page is mounted in, read from its own address.
 *
 * It used to come from the shell's outlet context, which is what made the
 * page's behaviour depend on being mounted under one — and the admin copy is
 * mounted directly now. The URL already says which tree the page is in, so
 * that is where the answer comes from. Teacher mode is read-only here
 * regardless of anything else.
 */
export function useWorkshopViewMode(): ViewMode {
  return modeFromPath(useLocation().pathname) ?? 'admin';
}

/** Context the detail page passes down to the nested `checkin` route (BON-204
 *  R2). The modal renders only when `canCheckIn` — a deep-link to `/checkin` on a
 *  read-only/non-active workshop falls back to the detail page. `closeHref` is where Done/× navigate back to. */
export interface CheckInRouteCtx {
  workshop: WorkshopDetail;
  canCheckIn: boolean;
  closeHref: string;
}

export function useCheckInRouteCtx() {
  return useOutletContext<CheckInRouteCtx>();
}
